/**
 * PositionControlPanel Component
 *
 * Control panel for adjusting puppet body part positions in real-time.
 * Allows users to manipulate the 3D model and find the correct starting position.
 *
 * Features:
 * - Slider for each body part with numeric value display
 * - Min/max angle limits from motor configuration
 * - Offset and direction controls for calibrating GLTF model rest pose
 * - Real-time 3D model updates
 * - Save as Starting Position button
 * - Reset to defaults button
 */

import { useCallback } from 'react';
import { usePuppetStore } from '../../stores/puppetStore';
import type {
  MotorBodyPart,
  MotorPositions,
  MotorAxis,
} from '../../stores/puppetStore';

/**
 * Props for the PositionControlPanel component
 */
export interface PositionControlPanelProps {
  /** Whether the panel is visible */
  visible: boolean;
  /** Callback to apply position changes to 3D model */
  onPositionChange: (positions: MotorPositions) => void;
  /** Current positions from animation state */
  currentPositions?: MotorPositions;
}

/**
 * All motor body parts in display order
 */
const BODY_PARTS: MotorBodyPart[] = [
  'head',
  'body',
  'leftArm',
  'rightArm',
  'leftLeg',
  'rightLeg',
];

/**
 * Display labels for body parts
 */
const BODY_PART_LABELS: Record<MotorBodyPart, string> = {
  head: 'Head',
  body: 'Body',
  leftArm: 'L.Arm',
  rightArm: 'R.Arm',
  leftLeg: 'L.Leg',
  rightLeg: 'R.Leg',
};

/**
 * Default positions (all zeros)
 */
const DEFAULT_POSITIONS: MotorPositions = {
  head: 0,
  body: 0,
  leftArm: 0,
  rightArm: 0,
  leftLeg: 0,
  rightLeg: 0,
};

/**
 * PositionControlPanel Component
 *
 * Displays sliders for each body part to control puppet position.
 */
export function PositionControlPanel({
  visible,
  onPositionChange,
  currentPositions,
}: PositionControlPanelProps): JSX.Element | null {
  // Get puppet config from store
  // Subscribe to motors array directly to ensure re-render on motor config changes
  const motors = usePuppetStore((state) => state.activePuppet?.motors);
  const updateStartingPosition = usePuppetStore(
    (state) => state.updateStartingPosition
  );
  const updateMotorConfig = usePuppetStore((state) => state.updateMotorConfig);

  // Use current positions or defaults
  const positions = currentPositions ?? DEFAULT_POSITIONS;

  const handleSliderChange = useCallback(
    (bodyPart: MotorBodyPart, value: number) => {
      const newPositions = { ...positions, [bodyPart]: value };
      onPositionChange(newPositions);
    },
    [positions, onPositionChange]
  );

  const handleOffsetChange = useCallback(
    (bodyPart: MotorBodyPart, offset: number) => {
      updateMotorConfig(bodyPart, { boneOffset: offset });
      onPositionChange({ ...positions });
    },
    [positions, onPositionChange, updateMotorConfig]
  );

  const handleXOffsetChange = useCallback(
    (bodyPart: MotorBodyPart, xOffset: number) => {
      updateMotorConfig(bodyPart, { xOffset });
      onPositionChange({ ...positions });
    },
    [positions, onPositionChange, updateMotorConfig]
  );

  const handleYOffsetChange = useCallback(
    (bodyPart: MotorBodyPart, yOffset: number) => {
      updateMotorConfig(bodyPart, { yOffset });
      onPositionChange({ ...positions });
    },
    [positions, onPositionChange, updateMotorConfig]
  );

  const handleZOffsetChange = useCallback(
    (bodyPart: MotorBodyPart, zOffset: number) => {
      updateMotorConfig(bodyPart, { zOffset });
      onPositionChange({ ...positions });
    },
    [positions, onPositionChange, updateMotorConfig]
  );

  const handleAxisChange = useCallback(
    (bodyPart: MotorBodyPart, axis: MotorAxis) => {
      updateMotorConfig(bodyPart, { axis });
      onPositionChange({ ...positions });
    },
    [positions, onPositionChange, updateMotorConfig]
  );

  const handleMinAngleChange = useCallback(
    (bodyPart: MotorBodyPart, minAngle: number) => {
      updateMotorConfig(bodyPart, { minAngle });
      onPositionChange({ ...positions });
    },
    [positions, onPositionChange, updateMotorConfig]
  );

  const handleMaxAngleChange = useCallback(
    (bodyPart: MotorBodyPart, maxAngle: number) => {
      updateMotorConfig(bodyPart, { maxAngle });
      onPositionChange({ ...positions });
    },
    [positions, onPositionChange, updateMotorConfig]
  );

  const handleDirectionChange = useCallback(
    (bodyPart: MotorBodyPart, direction: 1 | -1) => {
      updateMotorConfig(bodyPart, { direction });
      onPositionChange({ ...positions });
    },
    [positions, onPositionChange, updateMotorConfig]
  );

  const handleSaveAsStarting = useCallback(() => {
    BODY_PARTS.forEach((bodyPart) => {
      updateStartingPosition(bodyPart, positions[bodyPart]);
    });
  }, [positions, updateStartingPosition]);

  const handleReset = useCallback(() => {
    onPositionChange(DEFAULT_POSITIONS);
  }, [onPositionChange]);

  // Don't render if not visible (after all hooks)
  if (!visible) return null;

  const getMotorConfig = (bodyPart: MotorBodyPart) => {
    return motors?.find((m) => m.bodyPart === bodyPart);
  };

  return (
    <div
      data-testid="position-control-panel"
      className="w-[600px] rounded-lg bg-black/80 p-3 font-mono text-xs text-white shadow-lg"
    >
      {/* Header */}
      <div className="mb-3 border-b border-white/20 pb-2 text-sm font-semibold">
        Position Control
      </div>

      {/* Column headers */}
      <div className="mb-1 flex items-center gap-1 text-[10px] text-gray-500">
        <span className="w-12">Part</span>
        <span className="w-8 text-center">Axis</span>
        <span className="w-10 text-center">Min</span>
        <span className="w-10 text-center">Max</span>
        <span className="flex-1 text-center">Pos</span>
        <span className="w-10 text-center">Val</span>
        <span className="w-12 text-center">Offset</span>
        <span className="w-10 text-center">X</span>
        <span className="w-10 text-center">Y</span>
        <span className="w-10 text-center">Z</span>
        <span className="w-6 text-center">±</span>
      </div>

      {/* Body part sliders */}
      <div className="space-y-2">
        {BODY_PARTS.map((bodyPart) => {
          const config = getMotorConfig(bodyPart);
          const minAngle = config?.minAngle ?? -270;
          const maxAngle = config?.maxAngle ?? 270;
          const currentValue = Math.round(positions[bodyPart]);
          const axis = config?.axis ?? 'x';
          const offset = config?.boneOffset ?? 0;
          const xOffset = config?.xOffset ?? 0;
          const yOffset = config?.yOffset ?? 0;
          const zOffset = config?.zOffset ?? 0;
          const direction = config?.direction ?? 1;

          return (
            <div key={bodyPart} className="flex items-center gap-1">
              {/* Label */}
              <span className="w-12 text-gray-400">
                {BODY_PART_LABELS[bodyPart]}
              </span>

              {/* Axis selector */}
              <select
                value={axis}
                onChange={(e) =>
                  handleAxisChange(bodyPart, e.target.value as MotorAxis)
                }
                className="w-8 rounded bg-gray-800 px-0.5 py-0.5 text-center text-[10px] text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                title="Controlled rotation axis"
              >
                <option value="x">X</option>
                <option value="y">Y</option>
                <option value="z">Z</option>
              </select>

              {/* Min angle input */}
              <input
                type="number"
                value={minAngle}
                onChange={(e) =>
                  handleMinAngleChange(bodyPart, Number(e.target.value))
                }
                className="w-10 rounded bg-gray-800 px-1 py-0.5 text-center text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                title="Minimum angle (degrees)"
              />

              {/* Max angle input */}
              <input
                type="number"
                value={maxAngle}
                onChange={(e) =>
                  handleMaxAngleChange(bodyPart, Number(e.target.value))
                }
                className="w-10 rounded bg-gray-800 px-1 py-0.5 text-center text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                title="Maximum angle (degrees)"
              />

              {/* Position Slider */}
              <input
                type="range"
                min={minAngle}
                max={maxAngle}
                value={currentValue}
                onChange={(e) =>
                  handleSliderChange(bodyPart, Number(e.target.value))
                }
                className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-gray-700 accent-blue-500"
                style={{
                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((currentValue - minAngle) / (maxAngle - minAngle)) * 100}%, #374151 ${((currentValue - minAngle) / (maxAngle - minAngle)) * 100}%, #374151 100%)`,
                }}
              />

              {/* Position Value input */}
              <input
                type="number"
                min={minAngle}
                max={maxAngle}
                value={currentValue}
                onChange={(e) =>
                  handleSliderChange(bodyPart, Number(e.target.value))
                }
                className="w-10 rounded bg-gray-800 px-1 py-0.5 text-center text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />

              {/* Offset input (main axis) */}
              <input
                type="number"
                value={offset}
                onChange={(e) =>
                  handleOffsetChange(bodyPart, Number(e.target.value))
                }
                className="w-12 rounded bg-gray-800 px-1 py-0.5 text-center text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                title="Bone offset on controlled axis (degrees)"
              />

              {/* X Offset input (static) */}
              <input
                type="number"
                value={xOffset}
                onChange={(e) =>
                  handleXOffsetChange(bodyPart, Number(e.target.value))
                }
                className="w-10 rounded bg-gray-800 px-1 py-0.5 text-center text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                title="Static X offset (degrees)"
              />

              {/* Y Offset input (static) */}
              <input
                type="number"
                value={yOffset}
                onChange={(e) =>
                  handleYOffsetChange(bodyPart, Number(e.target.value))
                }
                className="w-10 rounded bg-gray-800 px-1 py-0.5 text-center text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                title="Static Y offset (degrees)"
              />

              {/* Z Offset input (static) */}
              <input
                type="number"
                value={zOffset}
                onChange={(e) =>
                  handleZOffsetChange(bodyPart, Number(e.target.value))
                }
                className="w-10 rounded bg-gray-800 px-1 py-0.5 text-center text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                title="Static Z offset (degrees)"
              />

              {/* Direction toggle */}
              <button
                onClick={() =>
                  handleDirectionChange(bodyPart, direction === 1 ? -1 : 1)
                }
                className={`w-6 rounded px-1 py-0.5 text-center font-bold transition-colors ${
                  direction === 1
                    ? 'bg-green-700 text-green-200'
                    : 'bg-red-700 text-red-200'
                }`}
                title={`Direction: ${direction === 1 ? 'positive' : 'negative'}`}
              >
                {direction === 1 ? '+' : '-'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Action buttons */}
      <div className="mt-3 flex flex-col gap-2 border-t border-white/20 pt-3">
        {/* Position buttons row */}
        <div className="flex gap-2">
          <button
            onClick={handleSaveAsStarting}
            className="flex-1 rounded bg-blue-600 px-2 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-500"
          >
            Save as Start
          </button>
          <button
            onClick={handleReset}
            className="flex-1 rounded bg-gray-700 px-2 py-1.5 text-xs font-medium text-gray-200 transition-colors hover:bg-gray-600"
          >
            Reset
          </button>
        </div>
        {/* Settings info - motor configs are auto-saved via Zustand persist */}
        <div className="text-center text-[10px] text-gray-500">
          Offset &amp; direction settings are saved automatically
        </div>
      </div>
    </div>
  );
}
